import { createContext, useContext, useState, useEffect } from "react";
import { upsertOrderData } from "../services/apiCalls"; 
import { useTranslation } from 'react-i18next';
import { resolveCartItemLanguage } from "../utils/resolveCartItemLanguage";

const ShoppingCartContext = createContext();

export const ShoppingCartProvider = ({ children, productsByType }) => {
  const { i18n } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const [interval, setInterval] = useState('year')
  const [subscription, setSubscription] = useState(null);   // single selected subscription
  const [addons, setAddons] = useState([]);                 // multiple add-ons
  const [caseId, setCaseId] = useState(null);
  const [taxResult, setTaxResult] = useState(null);
  const [taxNeedsRecalculation, setTaxNeedsRecalculation] = useState(true);
  const updateTaxResult = (result) => {
    setTaxResult(result);
    setTaxNeedsRecalculation(false);
  };
  const [validatedVat, setValidatedVat] = useState(null);
  const [validatedVatStatus, setValidatedVatStatus] = useState(null);
  const cacheVatValidation = (vat, status) => {
    setValidatedVat(vat);
    setValidatedVatStatus(status);
  };
  const defaultFormValues = {
      companyName: "",
      vatNumber: "",
      contactFirstName: "",
      contactLastName: "",
      billingEmail: "",
      phone: "",
      street: "",
      postalCode: "",
      city: "",
      country: "",
      countryISO:"",
      ggnNumber: "",
      activationCode: "",
      comment: "",
      allValid: false
  }
  const [formValues, setFormValues] = useState(defaultFormValues);

  // ---- Subscription ----
  const invalidateTax = () => {
    setTaxNeedsRecalculation(true); 
    // console.log('invalidateTax')
  }

  const selectSubscription = (item) => {
    setSubscription(item);
    invalidateTax();
  };

  const clearSubscription = () => {
    setSubscription(null);
    invalidateTax();
  };

  const retrieveLocalStorageState = () => {
    const saved = localStorage.getItem("shoppingCartState");
    if (saved) {
      console.log('local storage state found:', saved)
      const parsed = JSON.parse(saved);
      setSubscription(parsed.subscription || null);
      setAddons(parsed.addons || []);
      setFormValues(parsed.formValues || defaultFormValues);
      setInterval(parsed.interval || 'year');
      setCaseId(parsed.caseId || null);
      return parsed
    }
  }

  const setLocalStorageState = () => {
    const state = {
      subscription,
      addons,
      formValues,
      interval,
      caseId
    };
    localStorage.setItem("shoppingCartState", JSON.stringify(state));
  }

  // ---- Add-ons ----
  const addAddon = (addon) => {
    invalidateTax();

    setAddons((prev) => {
      const existing = prev.find((a) => a.id === addon.id);

      if (existing) {
        const updatedAddons = prev.map((a) =>
          a.id === addon.id ? { ...a, quantity: addon.quantity } : a
        );

        // If quantity increased, reset validity flags for any addon-specific extra info
        if (addon.quantity > existing.quantity) {
          setFormValues((fv) => ({
            ...fv,
            allAddonContactsValid: false,
            allAddonCompaniesValid: false
          }));
        }

        return updatedAddons;
      }

      return [...prev, addon];
    });
  };

  const removeAddon = (addonId) => {
    invalidateTax();
    setAddons((prev) => prev.filter((a) => a.id !== addonId));
  };

  const clearAddons = () => {
    invalidateTax();
    setAddons([]);
  };

  const changeformValues = (newValues) => {
    setFormValues(newValues);
    invalidateTax();
  };

  const changeInterval = (interval) => {
    setInterval(interval)
    invalidateTax();
    clearAddons()
  }

  const submitInformationForm = async () => {
    if (!formValues.allValid) {
      console.warn("Form is not valid, cannot submit");
      return;
    }

    if (isSubmitting) {
      // console.log("Already submitting — scheduling a follow-up submission");
      setPendingSubmit(true);
      return;
    }

    try {
      setIsSubmitting(true);

      // setLocalStorageState();

      const payload = {
        description: "",
        payload: JSON.stringify({
          subscription,
          addons,
          formValues,
          interval,
          language: i18n?.language?.toLocaleUpperCase() || 'EN'
        }),
        caseId: caseId,
      };

      // console.log("payload", payload);

      const result = await upsertOrderData(payload);
      // console.log("Form submitted successfully:", result);

      if (result.caseId) setCaseId(result.caseId);
      return result;
    } catch (error) {
      console.error("Error submitting form:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isSubmitting && pendingSubmit) {
      // console.log("Running queued submission");
      setPendingSubmit(false);
      submitInformationForm();
    }
  }, [isSubmitting, pendingSubmit]);


  // const setCaseStatusFulfilled = async () => {
  //   const localStorageResult = retrieveLocalStorageState()
  //   try {
  //     const payload = {
  //       caseId: caseId || localStorageResult.caseId,
  //       status: 'Fulfilled',
  //       billingEmail: formValues.billingEmail || localStorageResult.formValues.billingEmail,
  //       companyName: formValues.companyName || localStorageResult.formValues.companyName
  //     }

  //     console.log('payload',payload)

  //     const result = await setCaseStatus(payload)
  //   } catch (error) {
  //     console.error("Error updating ordercase:", error);
  //     throw error;
  //   }
  // }

  useEffect(() => {
    if (formValues.allValid) {
      // call your API automatically when the form becomes valid
      submitInformationForm();
    }
  }, [formValues.allValid])

  const applyActivationPrefill = () => {
    try {
      const storedActivationData = window.localStorage.getItem('activationData');
      if (!storedActivationData) return;

      const data = JSON.parse(storedActivationData);
      const storedActivationCode = window.localStorage.getItem('activationCode');
      setFormValues((prev) => ({
        ...prev,
        companyName: data?.accountName ?? prev.companyName,
        vatNumber: data?.accountVatNumber ?? prev.vatNumber,
        contactFirstName: data?.contactFirstName ?? prev.contactFirstName,
        contactLastName: data?.contactLastName ?? prev.contactLastName,
        billingEmail: data?.contactEmail ?? prev.billingEmail,
        ggnNumber: data?.accountGgn ?? prev.ggnNumber,
        activationCode: storedActivationCode || prev.activationCode,
      }));
    } catch (error) {
      console.error('Failed to load activation data', error);
    }
  };

  useEffect(() => {
    applyActivationPrefill();
  }, []);

  useEffect(() => {
    const handleActivationDataUpdated = () => {
      applyActivationPrefill();
    };

    window.addEventListener('activationDataUpdated', handleActivationDataUpdated);
    window.addEventListener('storage', handleActivationDataUpdated);

    return () => {
      window.removeEventListener('activationDataUpdated', handleActivationDataUpdated);
      window.removeEventListener('storage', handleActivationDataUpdated);
    };
  }, []);

  useEffect(() => {
    if (!productsByType) return;

    setSubscription((prev) =>
      resolveCartItemLanguage(prev, productsByType, i18n.language)
    );

    setAddons((prev) =>
      prev.map((addon) =>
        resolveCartItemLanguage(addon, productsByType, i18n.language)
      )
    );
  }, [i18n.language, productsByType]);

  return (
    <ShoppingCartContext.Provider
      value={{
        caseId,
        subscription,
        selectSubscription,
        clearSubscription,
        addons,
        addAddon,
        removeAddon,
        clearAddons,
        formValues,
        changeformValues,
        interval,
        changeInterval,
        submitInformationForm,
        isSubmitting,
        pendingSubmit,
        taxResult,
        taxNeedsRecalculation,
        updateTaxResult,
        validatedVat,
        validatedVatStatus,
        cacheVatValidation
      }}
    >
      {children}
    </ShoppingCartContext.Provider>
  );
};

export const useShoppingCart = () => useContext(ShoppingCartContext);